import React, { useState, useEffect } from "react";
import SEO from "../seo";
const TrackingEvents = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "available-tracking-options",
        "how-it-works",
        "key-benefits",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Tracking Events | xPage Help Center"
        description="Learn how to track customer behavior in Xpage using built-in event tracking tools. Connect major tracking pixels and analytics, measure conversions, get marketing insights, and manage all tools in one place."
        keywords="xPage tracking events, event tracking, Facebook Pixel, Google Analytics, Google Tag Manager, TikTok Pixel, Pinterest Pixel, Snapchat Pixel, marketing insights, conversion tracking"
        canonical="https://help.xpage.ai/help/settings/Tracking%20Events"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Tracking Events
          </h1>
          <p className="text-sm text-systemLabel">
            Xpage makes it simple to track customer behavior and measure
            marketing performance with built-in event tracking. All tracking
            tools are embedded directly into the system, so there's no need to
            install third-party apps or plugins.
          </p>
        </header>

        {/* Available Tracking Options Section */}
        <section id="available-tracking-options" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Available Tracking Options
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                You can connect and manage all major tracking pixels and
                analytics tools directly from Xpage:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Facebook Pixel
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Monitor conversions, optimize ads, and build audiences.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Google Analytics
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Gain insights into traffic, user behavior, and key
                      performance metrics.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Google Tag Manager
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Easily manage all your tags without touching code.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        TikTok Pixel
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Track conversions and optimize campaigns on TikTok.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Pinterest Pixel
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Measure performance of Pinterest ads and audience
                      engagement.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Snapchat Pixel
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Track actions from Snapchat ads to improve targeting.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How It Works
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 1
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Navigate to Settings → Tracking Events in your Xpage
                      dashboard.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 2
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Select the tracking tool you want to connect.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 3
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Enter the required ID or code for each pixel or analytics
                      account.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 4
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Xpage automatically activates the tracking across your
                      landing pages and store.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Key Benefits Section */}
        <section id="key-benefits" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Key Benefits
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        No Third-Party Apps Needed
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Everything is built into Xpage, reducing setup time and
                      complexity.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Real-Time Data
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Collect accurate event and conversion data immediately.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Marketing Insights
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Understand which channels drive traffic, conversions, and
                      revenue.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Simplified Management
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      All tracking tools are in one place for easy monitoring
                      and updates.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#available-tracking-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "available-tracking-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Available Tracking Options
              </a>
            </li>
            <li>
              <a
                href="#how-it-works"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "how-it-works"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                How It Works
              </a>
            </li>
            <li>
              <a
                href="#key-benefits"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "key-benefits"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Key Benefits
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TrackingEvents;
